import { useMutation, useQuery } from '@apollo/client/react';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { AccessNotice } from '@/components/AccessNotice/AccessNotice';
import { Button } from '@/components/Button/Button';
import { Callout } from '@/components/Callout/Callout';
import { EmptyState } from '@/components/EmptyState/EmptyState';
import { FactList } from '@/components/FactList/FactList';
import { Icon } from '@/components/Icon/Icon';
import { LinkDialog } from '@/components/LinkDialog/LinkDialog';
import { PageHeader } from '@/components/PageHeader/PageHeader';
import { RecordList } from '@/components/RecordList/RecordList';
import { Spinner } from '@/components/Spinner/Spinner';
import {
  ORGANIZATION_QUERY,
  SET_TENANT_ORGANIZATION,
  TENANTS_QUERY,
  type OrganizationResult,
  type SetTenantOrganizationResult,
  type TenantsResult,
} from '@/graphql/tenants';
import { formatDate } from '@/lib/format';
import { friendlyError, isPermissionDenied } from '@/lib/keystone-errors';
import { displayName, sortByName, tenantCount } from '@/lib/tenants';
import { useResourceAccess } from '@/lib/use-resource-access';
import { useToast } from '@/toast/useToast';

import styles from './TenantsContainer.module.css';

/** `/organizations/:id`: one organization, its tenants, and linking another to it. */
export function OrganizationContainer() {
  const { id = '' } = useParams();
  const toast = useToast();
  const { access, refetch: recheckAccess } = useResourceAccess('tenants');
  const canRead = access === 'read' || access === 'full';
  const canEdit = access === 'full';

  const [linking, setLinking] = useState(false);
  const [linkError, setLinkError] = useState<string | null>(null);

  const { data, loading, error, refetch } = useQuery<OrganizationResult>(ORGANIZATION_QUERY, {
    variables: { id },
    skip: !canRead,
    fetchPolicy: 'cache-and-network',
  });
  const tenants = useQuery<TenantsResult>(TENANTS_QUERY, {
    skip: !linking,
    fetchPolicy: 'cache-and-network',
  });
  const [setOrganization, { loading: saving }] =
    useMutation<SetTenantOrganizationResult>(SET_TENANT_ORGANIZATION);

  const back = <Link to="/organizations">← All organizations</Link>;

  if (!canRead) {
    return (
      <AccessNotice area="organizations" onRetry={recheckAccess} state={access as 'checking'} />
    );
  }

  if (error && !data) {
    return (
      <>
        <PageHeader back={back} title="Organization" />
        <Callout tone="danger" title="This organization couldn't be loaded">
          {friendlyError(error)}
        </Callout>
      </>
    );
  }

  if (loading && !data) {
    return (
      <>
        <PageHeader back={back} title="Organization" />
        <p className={styles.loading} role="status">
          <Spinner /> Loading organization…
        </p>
      </>
    );
  }

  const organization = data?.organization;
  if (!organization) {
    return null;
  }
  const name = displayName(organization);
  const linkedIds = new Set(organization.tenants.map((tenant) => tenant.id));

  const closeLink = () => {
    setLinking(false);
    setLinkError(null);
  };

  const onLink = async (tenantId: string) => {
    setLinkError(null);
    try {
      const result = await setOrganization({
        variables: { tenantId, organizationId: organization.id },
      });
      await refetch();
      closeLink();
      const tenant = result.data?.setTenantOrganization;
      toast.success({ title: `${tenant ? displayName(tenant) : 'Tenant'} linked to ${name}` });
    } catch (cause) {
      setLinkError(friendlyError(cause));
      if (isPermissionDenied(cause)) {
        recheckAccess();
      }
    }
  };

  const candidates = sortByName(
    (tenants.data?.brokerages ?? []).filter((tenant) => !linkedIds.has(tenant.id)),
  ).map(
    (tenant) =>
      [
        tenant.id,
        tenant.organization
          ? `${displayName(tenant)} (moves from ${displayName(tenant.organization)})`
          : displayName(tenant),
      ] as const,
  );

  const linkButton = canEdit ? (
    <Button
      icon={<Icon name="plus" />}
      loading={linking && !tenants.data}
      onClick={() => setLinking(true)}
      variant="ghost"
    >
      Link a tenant
    </Button>
  ) : null;

  return (
    <div className={styles.page}>
      <PageHeader back={back} title={name} />
      <FactList
        facts={[
          { label: 'Name', value: organization.name },
          { label: 'Slug', value: organization.slug, code: true },
          { label: 'app-api id', value: organization.extAppApiId, code: true },
          { label: 'Keystone id', value: organization.id, code: true },
          { label: 'Created', value: formatDate(organization.createdAt) },
        ]}
        label="Organization details"
      />

      <section aria-labelledby="organization-tenants" className={styles.section}>
        <div className={styles.sectionHead}>
          <div>
            <h2 className={styles.sectionTitle} id="organization-tenants">
              Tenants
            </h2>
            <p className={styles.sectionLede}>{tenantCount(organization.tenants.length)}</p>
          </div>
          {organization.tenants.length > 0 ? linkButton : null}
        </div>
        {organization.tenants.length === 0 ? (
          <EmptyState action={linkButton} icon="buildings" title="No tenants linked">
            <p>Link a tenant to put it under {name}.</p>
          </EmptyState>
        ) : (
          <RecordList
            items={sortByName(organization.tenants).map((tenant) => ({
              id: tenant.id,
              title: displayName(tenant),
              meta: tenant.extAppApiId ? `app-api ${tenant.extAppApiId}` : null,
            }))}
            label={`Tenants of ${name}`}
            renderLink={(item, children) => <Link to={`/tenants/${item.id}`}>{children}</Link>}
          />
        )}
      </section>

      {linking && tenants.error && !tenants.data ? (
        <div className={styles.section}>
          <Callout tone="danger" title="Tenants couldn't be loaded">
            {friendlyError(tenants.error)}
          </Callout>
        </div>
      ) : null}

      {linking && tenants.data ? (
        <LinkDialog
          busy={saving}
          confirmLabel="Link"
          description={
            candidates.length === 0
              ? 'Every tenant is already linked to this organization.'
              : 'A tenant already under another organization moves here.'
          }
          error={linkError}
          label="Tenant"
          onCancel={closeLink}
          onSubmit={onLink}
          options={candidates}
          title={`Link a tenant to ${name}`}
        />
      ) : null}
    </div>
  );
}
